import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Zap, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PAYMENTS_ROUTE } from "@/constants/RoutesConfig";
import { useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { getSubscriptions, getUsersTokensUtilization } from "@/store/actions/meta.action";
import PaymentService from "@/services/modules/payments.service";
import { CommonService } from "@/services";
import AlertService from "@/services/alert.service";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";


const PlansAndBillingSettings = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector((state) => state.auth?.user);
    
    // Get data from Redux
    const subscriptions = useAppSelector((state) => state.meta.subscriptions);
    const subscriptionsLoading = useAppSelector((state) => state.meta.subscriptionsLoading);
    const subscriptionsError = useAppSelector((state) => state.meta.subscriptionsError);
    const usersBalance = useAppSelector((state) => state.meta.usersBalance);
    const balanceLoading = useAppSelector((state) => state.meta.usersBalanceLoading);
    const balanceError = useAppSelector((state) => state.meta.usersBalanceError);

    // Cancel subscription state
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);

    // Fetch data on mount
    useEffect(() => {
        dispatch(getSubscriptions());
        dispatch(getUsersTokensUtilization());
    }, [dispatch]);

    // Determine current plan
    const userSubscription = currentUser?.subscription;
    const currentPlanId = userSubscription?.plan_id;
    
    // Get plan details from subscriptions list using plan_id
    const currentPlanDetails = Array.isArray(subscriptions) 
        ? (subscriptions as any[]).find((sub: any) => sub.id === currentPlanId)
        : null;
    
    const currentPlanName = currentPlanDetails?.plan_name || userSubscription?.plan_name || "Free";
    const isFreeUser = !currentPlanId || currentPlanName.toLowerCase() === "free" || !userSubscription;
    
    const handleUpgradeClick = () => {
        navigate(PAYMENTS_ROUTE);
    };

    const handleCancelSubscription = async () => {
        if (!userSubscription?.subscription_id) {
            AlertService.showToast("No active subscription found", "error");
            return;
        }

        setIsCanceling(true);
        try {
            await PaymentService.cancelSubscription(userSubscription.subscription_id);
            
            // Track successful cancellation
            CommonService.handleMixpanelTrack("Subscription Cancelled", {
                user_id: currentUser?.id,
                subscription_id: userSubscription?.subscription_id,
                plan_name: currentPlanName,
                timestamp: new Date().toISOString()
            });

            // Show success message
            AlertService.showToast("Your subscription has been cancelled. You will retain access until the end of your current billing cycle.", "success");

            // Close dialog and refresh data
            setIsCancelDialogOpen(false);
            dispatch(getSubscriptions());
            dispatch(getUsersTokensUtilization());
        } catch (error: any) {
            console.error("Error canceling subscription:", error);
            AlertService.showToast(error?.response?.data?.message || "Failed to cancel subscription. Please try again.", "error");
        } finally {
            setIsCanceling(false);
        }
    };

    const loading = subscriptionsLoading || balanceLoading;
    const error = subscriptionsError || balanceError;

    if (loading) {
        return (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-[var(--foreground)]">Plans & Billing</h3>
                    <p className="text-sm text-[var(--muted-foreground)]">
                        Manage your subscription and billing information
                    </p>
                </div>
                <div className="space-y-4">
                    {/* Current Plan Card Skeleton */}
                    <Card className="border-[var(--border)] bg-[var(--card)]">
                        <CardHeader>
                            <Skeleton className="h-6 w-32" />
                            <Skeleton className="h-4 w-64 mt-2" />
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between p-4 rounded-lg border border-[var(--border)]">
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-40" />
                                    <Skeleton className="h-4 w-56" />
                                </div>
                                <Skeleton className="h-10 w-32" />
                            </div>
                            <div className="space-y-2">
                                <Skeleton className="h-4 w-40" />
                                <div className="space-y-2">
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-full" />
                                    <Skeleton className="h-4 w-3/4" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Credits Card Skeleton */}
                    <Card className="border-[var(--border)] bg-[var(--card)]">
                        <CardHeader>
                            <Skeleton className="h-6 w-36" />
                            <Skeleton className="h-4 w-56 mt-2" />
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-6">
                                <div className="flex items-center justify-between p-4 rounded-lg border border-[var(--border)]">
                                    <Skeleton className="h-4 w-32" />
                                    <Skeleton className="h-8 w-24" />
                                </div>
                                <div className="flex items-center justify-between p-4 rounded-lg border border-[var(--border)]">
                                    <Skeleton className="h-4 w-28" />
                                    <Skeleton className="h-6 w-20" />
                                </div>
                                <div className="space-y-3 pt-2 border-t border-[var(--border)]">
                                    <Skeleton className="h-4 w-36" />
                                    <div className="flex items-center justify-between">
                                        <Skeleton className="h-4 w-24" />
                                        <Skeleton className="h-4 w-16" />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Skeleton className="h-4 w-36" />
                                        <Skeleton className="h-4 w-16" />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <Skeleton className="h-4 w-28" />
                                        <Skeleton className="h-4 w-16" />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-[var(--foreground)]">Plans & Billing</h3>
                    <p className="text-sm text-[var(--muted-foreground)]">
                        Manage your subscription and billing information
                    </p>
                </div>
                <div className="flex items-center justify-center py-8">
                    <div className="text-center space-y-2">
                        <div className="text-sm text-red-500">Error: {error}</div>
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                dispatch(getSubscriptions());
                                dispatch(getUsersTokensUtilization());
                            }}
                            mixpanelEvent={{
                                name: "subscription_data_retry_clicked",
                                properties: { source: "plans_and_billing_settings" }
                            }}
                        >
                            Retry
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-[var(--foreground)]">Plans & Billing</h3>
                <p className="text-sm text-[var(--muted-foreground)]">
                    Manage your subscription and billing information
                </p>
            </div>
            <div className="space-y-4">
                {/* Current Plan Card */}
                <Card className="border-[var(--border)] bg-[var(--card)]">
                    <CardHeader>
                        <CardTitle className="text-[var(--foreground)]">Current Plan</CardTitle>
                        <CardDescription className="text-[var(--muted-foreground)]">
                            You are currently on the {currentPlanName} plan
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between p-4 rounded-lg border border-[var(--border)]">
                            <div className="space-y-1">
                                <div className="text-2xl font-bold text-[var(--foreground)]">{currentPlanName} Plan</div>
                                <div className="text-sm text-[var(--muted-foreground)]">
                                    {currentPlanDetails?.plan_details?.description || 
                                        (isFreeUser ? "Get started with basic features" : "Premium features unlocked")}
                                </div>
                            </div>
                            {isFreeUser ? (
                                <Button 
                                    onClick={handleUpgradeClick}
                                    className="gap-2"
                                    mixpanelEvent={{name: "upgrade_from_settings_clicked"}}
                                >
                                    <Zap className="h-4 w-4" />
                                    Upgrade Plan
                                </Button>
                            ) : (
                                <Button 
                                    variant="outline"
                                    onClick={() => {
                                        setIsCancelDialogOpen(true);
                                    }}
                                    className="gap-2 text-red-500 hover:text-red-600 hover:border-red-500"
                                    mixpanelEvent={{name: "cancel_subscription_button_clicked"}}
                                >
                                    <X className="h-4 w-4" />
                                    Cancel Subscription
                                </Button>
                            )}
                        </div>

                        {/* Plan Features */}
                        {currentPlanDetails?.plan_details?.features && (
                            <div className="space-y-2">
                                <div className="text-sm font-medium text-[var(--foreground)]">
                                    {currentPlanName} Plan Includes:
                                </div>
                                <ul className="space-y-2">
                                    {currentPlanDetails.plan_details.features.map((feature: string, index: number) => (
                                        <li key={index} className="flex items-center gap-2 text-sm text-[var(--foreground)]">
                                            <Check className="h-4 w-4 text-[var(--primary)]" />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Credits Card */}
                <Card className="border-[var(--border)] bg-[var(--card)]">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-[var(--foreground)]">Credits Balance</CardTitle>
                                <CardDescription className="text-[var(--muted-foreground)]">
                                    Monitor your credit usage and balance
                                </CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-6">
                            {/* Remaining Credits */}
                            <div className="flex items-center justify-between p-4 rounded-lg border border-[var(--primary)]/20 bg-[var(--primary)]/10">
                                <span className="text-sm font-medium text-[var(--foreground)]">Remaining Credits</span>
                                <span className="text-2xl font-bold text-[var(--primary)]">
                                    ${(usersBalance?.user_remaining_budget ?? 0).toFixed(2)}
                                </span>
                            </div>

                            {/* Used Credits */}
                            <div className="flex items-center justify-between p-4 rounded-lg border border-[var(--border)]">
                                <span className="text-sm font-medium text-[var(--foreground)]">Used Credits</span>
                                <span className="text-lg font-semibold text-[var(--muted-foreground)]">
                                    ${(usersBalance?.user_used_budget ?? 0).toFixed(2)}
                                </span>
                            </div>

                            {/* Credits Breakdown */}
                            <div className="space-y-3 pt-2 border-t border-[var(--border)]">
                                <div className="text-sm font-medium text-[var(--foreground)]">Credits Breakdown</div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-[var(--muted-foreground)]">Gift Credits</span>
                                    <span className="text-sm font-medium text-[var(--foreground)]">
                                        ${(usersBalance?.gift_credits ?? 0).toFixed(2)}
                                    </span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-[var(--muted-foreground)]">Subscription Credits</span>
                                    <span className="text-sm font-medium text-[var(--foreground)]">
                                        ${(usersBalance?.subscription_credits ?? 0).toFixed(2)}
                                    </span>
                                </div>
                                
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-[var(--muted-foreground)]">Top-up Credits</span>
                                    <span className="text-sm font-medium text-[var(--foreground)]">
                                        ${(usersBalance?.topup_credits ?? 0).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Billing History Card */}
                {/* <Card className="border-[var(--border)] bg-[var(--card)]">
                    <CardHeader>
                        <CardTitle className="text-[var(--foreground)]">Billing History</CardTitle>
                        <CardDescription className="text-[var(--muted-foreground)]">
                            View your past invoices and payments
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="text-sm text-[var(--muted-foreground)]">
                            No billing history available
                        </div>
                        <Button 
                            variant="outline" 
                            className="mt-4 gap-2"
                            mixpanelEvent={{
                                name: "view_all_invoices_clicked",
                                properties: { source: "plans_and_billing_settings" }
                            }}
                        >
                            <CreditCard className="h-4 w-4" />
                            View All Invoices
                        </Button>
                    </CardContent>
                </Card> */}
            </div>
        </div>

        {/* Cancel Subscription Dialog */}
        <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">Cancel Subscription</DialogTitle>
                    <DialogDescription className="text-base pt-2">
                        Are you sure you want to cancel your subscription?
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-2">
                        <p className="text-sm font-medium text-yellow-800">Important Information:</p>
                        <p className="text-sm text-yellow-700">
                            You will still be active until the end of your billing cycle and will not be charged after that.
                        </p>
                    </div>
                    <div className="space-y-2 text-sm text-[var(--muted-foreground)]">
                        <p>• Your access to premium features will continue until your billing period ends</p>
                        <p>• No future charges will be applied to your account</p>
                        <p>• You can resubscribe at any time if you change your mind</p>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        variant="destructive"
                        onClick={handleCancelSubscription}
                        disabled={isCanceling}
                    >
                        {isCanceling ? "Canceling..." : "Yes, Cancel Subscription"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setIsCancelDialogOpen(false)}
                        disabled={isCanceling}
                    >
                        Keep Subscription
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
};

export default PlansAndBillingSettings;